import type { SceneObject } from "./SceneHost";
import {
  applyPathGeometry,
  clonePoints,
  isPathObject,
  translatePathPoints,
} from "./pathGeometry";
import { applySegmentPoints, segmentGeometryPatch } from "./bindings/resolveBindings";
import { isSegmentObject } from "./segmentGeometry";
import { objectBounds, unionBounds, type Bounds } from "./selectionBounds";

export type AlignMode = "left" | "center" | "right" | "top" | "middle" | "bottom";
export type DistributeMode = "horizontal" | "vertical";

function alignDelta(mode: AlignMode, union: Bounds, b: Bounds): { dx: number; dy: number } {
  switch (mode) {
    case "left":
      return { dx: union.x - b.x, dy: 0 };
    case "center":
      return { dx: union.x + union.w / 2 - (b.x + b.w / 2), dy: 0 };
    case "right":
      return { dx: union.x + union.w - (b.x + b.w), dy: 0 };
    case "top":
      return { dx: 0, dy: union.y - b.y };
    case "middle":
      return { dx: 0, dy: union.y + union.h / 2 - (b.y + b.h / 2) };
    case "bottom":
      return { dx: 0, dy: union.y + union.h - (b.y + b.h) };
  }
}

function patchForTranslate(obj: SceneObject, dx: number, dy: number): Record<string, unknown> | null {
  const rdx = Math.round(dx);
  const rdy = Math.round(dy);
  if (rdx === 0 && rdy === 0) return null;

  if (isPathObject(obj)) {
    const clone = JSON.parse(JSON.stringify(obj)) as SceneObject & {
      points: [number, number][];
    };
    const points = translatePathPoints(clonePoints(clone.points), rdx, rdy);
    applyPathGeometry(clone, points);
    return {
      x: clone.x,
      y: clone.y,
      w: clone.w,
      h: clone.h,
      points: clone.points,
    };
  }

  if (isSegmentObject(obj)) {
    const clone = JSON.parse(JSON.stringify(obj)) as SceneObject;
    const points = translatePathPoints(clonePoints(clone.points as [number, number][]), rdx, rdy);
    const a = points[0]!;
    const b = points[points.length - 1]!;
    applySegmentPoints(clone, a, b);
    return segmentGeometryPatch(clone);
  }

  return {
    x: Math.round(obj.x + rdx),
    y: Math.round(obj.y + rdy),
  };
}

function collectPatches(
  items: { obj: SceneObject; dx: number; dy: number }[]
): { id: string; patch: Record<string, unknown> }[] {
  const patches: { id: string; patch: Record<string, unknown> }[] = [];
  for (const item of items) {
    const patch = patchForTranslate(item.obj, item.dx, item.dy);
    if (patch) patches.push({ id: item.obj.id, patch });
  }
  return patches;
}

/** Патчи для выравнивания незаблокированных объектов по общей рамке выделения. */
export function buildAlignPatches(
  objects: SceneObject[],
  mode: AlignMode
): { id: string; patch: Record<string, unknown> }[] {
  const movable = objects.filter((o) => !o.locked);
  if (movable.length < 2) return [];

  const union = unionBounds(movable);
  if (!union || union.w < 0 || union.h < 0) return [];

  return collectPatches(
    movable.map((obj) => {
      const b = objectBounds(obj);
      const { dx, dy } = alignDelta(mode, union, b);
      return { obj, dx, dy };
    })
  );
}

/**
 * Равномерные промежутки между объектами внутри текущей общей рамки.
 * Крайние остаются на месте, средние раскладываются.
 * Нужно минимум 3 объекта.
 */
export function buildDistributePatches(
  objects: SceneObject[],
  mode: DistributeMode
): { id: string; patch: Record<string, unknown> }[] {
  const movable = objects.filter((o) => !o.locked);
  if (movable.length < 3) return [];

  const annotated = movable.map((obj) => ({ obj, b: objectBounds(obj) }));

  if (mode === "horizontal") {
    annotated.sort((a, b) => a.b.x - b.b.x || a.b.y - b.b.y);
    const first = annotated[0]!;
    const last = annotated[annotated.length - 1]!;
    const totalGaps = annotated.length - 1;
    const innerWidth = annotated.reduce((sum, it) => sum + it.b.w, 0);
    const span = last.b.x + last.b.w - first.b.x;
    const gap = (span - innerWidth) / totalGaps;
    const moves: { obj: SceneObject; dx: number; dy: number }[] = [];
    let x = first.b.x + first.b.w + gap;
    for (let i = 1; i < annotated.length - 1; i++) {
      const it = annotated[i]!;
      moves.push({ obj: it.obj, dx: x - it.b.x, dy: 0 });
      x += it.b.w + gap;
    }
    return collectPatches(moves);
  }

  annotated.sort((a, b) => a.b.y - b.b.y || a.b.x - b.b.x);
  const first = annotated[0]!;
  const last = annotated[annotated.length - 1]!;
  const totalGaps = annotated.length - 1;
  const innerHeight = annotated.reduce((sum, it) => sum + it.b.h, 0);
  const span = last.b.y + last.b.h - first.b.y;
  const gap = (span - innerHeight) / totalGaps;
  const moves: { obj: SceneObject; dx: number; dy: number }[] = [];
  let y = first.b.y + first.b.h + gap;
  for (let i = 1; i < annotated.length - 1; i++) {
    const it = annotated[i]!;
    moves.push({ obj: it.obj, dx: 0, dy: y - it.b.y });
    y += it.b.h + gap;
  }
  return collectPatches(moves);
}
